import useStore from "../store/useStore";
import { addApiLog } from "../utilities/networkLogger";

const originalFetch: any = fetch;
const fetchWithRetry = require('fetch-retry')(originalFetch, {  
    retries: 5,
    retryDelay: (attempt: number, error: any, response: any) => {
        return Math.pow(2, attempt) * 1000;
    }  
});

export const Regex = {
    id: /^[a-z0-9]+$/,
    // Digits only, 7-15 long (E.164 national-number bound) -- loosened from an
    // India-only 9-10 digit pattern now that farmers/staff can be from other
    // countries. Matches fyllo-core's FarmUser.mobile jsonSchema pattern.
    mobile: /^[0-9]{7,15}$/,
    latitude: /^(\+|-)?(?:90(?:(?:\.0{1,10})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,10})?))$/,
    longitude: /^(\+|-)?(?:180(?:(?:\.0{1,10})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,10})?))$/,
    // Covers both Instagram (letters/digits/./_ , 1-30 chars) and Facebook
    // (letters/digits/. , 5-50 chars) usernames -- the safe intersection: no
    // spaces, no leading/trailing period, no consecutive periods. Slightly
    // stricter than Facebook alone (allows leading digit/underscore/period,
    // which Facebook forbids) since this one field is stored under both
    // instagramHandle and facebookHandle and shouldn't reject a valid Insta
    // handle for not looking like a Facebook one.
    socialHandle: /^(?!\.)(?!.*\.\.)[a-zA-Z0-9._]{1,30}(?<!\.)$/
};

export interface APIOptions {
    basePath?: string,
    sendTokenInUrl?: boolean,
    sendTokenInHeader?: boolean,
    body?: any,
    headers?: any,
    usePutMethod?: boolean
    usePatchMethod?: boolean
}

export async function handleErrors(response: Response) {
    if (!response.ok) {
        const err = await response.json();
        throw Error(err.error.message);
    }
    return response.status === 200 ? response.json() : undefined;
}

function getAccessToken(): string {
    return localStorage.getItem('access_token') ?? '';
}

export function setAccessToken(access_token: string) {
    localStorage.setItem('access_token', access_token);
}

export function clearAccessToken() {
    localStorage.removeItem('access_token');
}

export function clearLocalStorage() {
    localStorage.clear();
}

async function handleUnauthorized() {
    if (!getAccessToken()) return;
    clearLocalStorage();
    await clearIndexDB();
    window.location.reload();
}
export function clearFarmUserData() {
    localStorage.removeItem('FarmUserData');
}
export async function clearIndexDB(){
    const { clearCache } = useStore.getState()
    await clearCache();
}

function addAccessToken(url: string): string {
    const qsSeperator = url.includes('?') ? '&' : '?';
    return url.concat(qsSeperator + 'access_token=' + getAccessToken());
}

const defaultCacheExpiryTime=7*86400*1000;

export async function getData(url = '', options: APIOptions = {},apiCache="", cacheExpireTime=defaultCacheExpiryTime,timeout=10000) {
    const basePath = options.basePath || process.env.REACT_APP_API_BASEPATH;
    const tokenRequiredInUrl = !!options.sendTokenInUrl;
    if (tokenRequiredInUrl) {
        url = addAccessToken(url);
    }
    const finalUrl = `${basePath}${url}`;
    const finalOptions: any = { method: 'get' };
    if (options.headers) {
        finalOptions.headers = options.headers;
    }
    const tokenRequiredInHeader = !!options.sendTokenInHeader;
    if (tokenRequiredInHeader) {
        finalOptions.headers = finalOptions.headers || {};
        finalOptions.headers['Authorization'] = `Bearer ${getAccessToken()}`
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => { controller.abort(); }, timeout);
    finalOptions.signal = controller.signal;

    try {
        if (!!apiCache) {
            const { fetchData } = useStore.getState();
            const responseIamgetting = await fetchData(apiCache, finalUrl, cacheExpireTime, finalOptions);
            addApiLog({ method: 'GET', url: finalUrl, status: 200, response: responseIamgetting, timestamp: new Date().toISOString() });
            return responseIamgetting;
        }

        const abortPromise = new Promise((_, reject) => {
            controller.signal.addEventListener("abort", () => {
                reject(new Error("Request aborted"));
            });
        });

        const rawResponse = await Promise.race([abortPromise, fetchWithRetry(finalUrl, finalOptions)]) as Response;
        let responseData: any = null;
        try {
            responseData = await rawResponse.clone().json();
        } catch {
            responseData = `[status: ${rawResponse.status}]`;
        }
        addApiLog({ method: 'GET', url: finalUrl, status: rawResponse.status, response: responseData, timestamp: new Date().toISOString() });
        if (rawResponse.status === 401) { handleUnauthorized(); }
        return rawResponse;
    } finally {
        clearTimeout(timeoutId);
    }
}


export async function postData(url = '', options: APIOptions = {}) {
    const basePath = options.basePath || process.env.REACT_APP_API_BASEPATH;
    const tokenRequiredInUrl = !!options.sendTokenInUrl;
    if (tokenRequiredInUrl) {
        url = addAccessToken(url);
    }
    const finalUrl = `${basePath}${url}`;
    const finalOptions: any = { method: options.usePutMethod ? 'put' : options.usePatchMethod ? 'PATCH' : 'post' };
    if (options.headers) {
        finalOptions.headers = options.headers;
    }
    const tokenRequiredInHeader = !!options.sendTokenInHeader;
    if (tokenRequiredInHeader) {
        finalOptions.headers = finalOptions.headers || {};
        finalOptions.headers['Authorization'] = `Bearer ${getAccessToken()}`
    }
    if (options.body) {
        finalOptions.body = finalOptions.headers['Content-Type'] === 'application/json'
            ? JSON.stringify(options.body)
            : options.body;
    }
    const postResponse = await fetch(finalUrl, finalOptions);
    let postResponseData: any = null;
    try {
        postResponseData = await postResponse.clone().json();
    } catch {
        postResponseData = `[status: ${postResponse.status}]`;
    }
    addApiLog({ method: finalOptions.method.toUpperCase(), url: finalUrl, status: postResponse.status, response: postResponseData, timestamp: new Date().toISOString() });
    if (postResponse.status === 401) { handleUnauthorized(); }
    return postResponse;
}

export async function deleteData(url = '', options: APIOptions = {}) {
    const basePath = options.basePath || process.env.REACT_APP_API_BASEPATH;
    const tokenRequiredInUrl = !!options.sendTokenInUrl;
    if (tokenRequiredInUrl) {
        url = addAccessToken(url);
    }
    const finalUrl = `${basePath}${url}`;
    const finalOptions: any = { method: 'delete' };
    if (options.headers) {
        finalOptions.headers = options.headers;
    }
    const tokenRequiredInHeader = !!options.sendTokenInHeader;
    if (tokenRequiredInHeader) {
        finalOptions.headers = finalOptions.headers || {};
        finalOptions.headers['Authorization'] = `Bearer ${getAccessToken()}`
    }
    const deleteResponse = await fetchWithRetry(finalUrl, finalOptions);
    let deleteResponseData: any = null;
    try {
        deleteResponseData = await deleteResponse.clone().json();
    } catch {
        deleteResponseData = `[status: ${deleteResponse.status}]`;
    }
    addApiLog({ method: 'DELETE', url: finalUrl, status: deleteResponse.status, response: deleteResponseData, timestamp: new Date().toISOString() });
    if (deleteResponse.status === 401) { handleUnauthorized(); }
    return deleteResponse;
}

export const toInputLowercase = (e: any) => {
    e.target.value = ('' + e.target.value).toLowerCase();
};